import {
  CAlert,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLink,
  CLoadingButton,
} from '@coreui/react-pro'

import React, { useEffect, useState, useMemo, useRef } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import GamePrizeService from 'src/service/GamePrizeService'
import ToastComponent from 'src/components/common/TaostComponent'
import SponsorService from 'src/service/SponsorService'
import ReactQuill from 'react-quill'

const EditForm = (props) => {
  const [prizeDetail, setPrizeDetail] = useState()
  const [preview, setPreview] = useState(null)
  const [loader, setLoader] = useState(false)
  const [errorMessages, setErrorMessages] = useState([])
  const quillRef = useRef()

  // ✅ FETCH LIKE ROUND FORM
  useEffect(() => {
    if (!props.prizeId) return

    GamePrizeService.getPrizeDetail(props.prizeId)
      .then((res) => {
        if (res.status === 200) {
          setPrizeDetail(res.data)
          setPreview(res.data.image)
        }
      })
      .catch((e) => {
        ToastComponent(e.response?.data?.message, 'error')
      })
  }, [props.prizeId])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: prizeDetail?.title || '',
      about: prizeDetail?.description || '',
      prize_type: prizeDetail?.prize_type || '',
      image: null,
    },
    validationSchema,
    onSubmit: (data) => {
      const formData = new FormData()
      formData.append('prizeId', props.prizeId)
      formData.append('name', data.name)
      formData.append('about', data.about)
      formData.append('prize_type', data.prize_type)
      if (data.image) formData.append('image', data.image)

      setLoader(true)

      GamePrizeService.editPrize(formData)
        .then((res) => {
          if (res.status === 200) {
            props.fetchUsers?.() // ✅ refresh list
            props.toggleDetails?.(props.prizeId) // ✅ close accordion
            ToastComponent(res.message, 'success')
            setLoader(false)
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong']

            setErrorMessages(errors)
            setLoader(false)
          }
        })
        .catch(() => {
          ToastComponent('Something went wrong', 'error')
          setLoader(false)
        })
    },
  })

  // ✅ QUILL IMAGE HANDLER
  const imageHandler = () => {
    const editor = quillRef.current.getEditor()
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()

    input.onchange = async () => {
      const file = input.files[0]
      if (/^image\//.test(file.type)) {
        const formData = new FormData()
        formData.append('image', file)
        const res = await SponsorService.imageUplaod(formData)
        const url = res?.url
        editor.insertEmbed(editor.getSelection(), 'image', url)
      } else {
        ToastComponent('Only images allowed', 'error')
      }
    }
  }

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['image', 'link'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [],
  )

  return (
    <>
      {/* ✅ ERROR UI SAME AS ROUND */}
      {errorMessages.length > 0 && (
        <div>
          {errorMessages.map((err, i) => (
            <CAlert color="danger" key={i}>
              {err}
            </CAlert>
          ))}
        </div>
      )}

      <CForm className="row g-3" onSubmit={formik.handleSubmit}>
        {/* NAME */}
        <CCol md={12}>
          <CFormLabel className="fw-bold">Prize Name *</CFormLabel>
          <CFormInput
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            className={formik.errors.name && formik.touched.name ? 'is-invalid' : ''}
          />
          {formik.errors.name && formik.touched.name && (
            <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
          )}
        </CCol>

        {/* TYPE */}
        <CCol md={12}>
          <CFormLabel className="fw-bold">Prize Type</CFormLabel>
          <CFormInput
            name="prize_type"
            value={formik.values.prize_type}
            onChange={formik.handleChange}
          />
        </CCol>

        {/* DESCRIPTION */}
        <CCol md={12}>
          <CFormLabel className="fw-bold">Prize Description</CFormLabel>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={formik.values.about}
            onChange={(val) => formik.setFieldValue('about', val)}
            modules={modules}
          />
        </CCol>

        {/* IMAGE */}
        <CCol md={12}>
          <CFormLabel className="fw-bold">Prize Image</CFormLabel>
          <CFormInput
            type="file"
            onChange={(e) => {
              const file = e.target.files[0]
              formik.setFieldValue('image', file)

              if (file) {
                const reader = new FileReader()
                reader.onloadend = () => setPreview(reader.result)
                reader.readAsDataURL(file)
              }
            }}
          />

          {preview && (
            <img src={preview} alt="preview" style={{ width: 100, height: 100, marginTop: 10 }} />
          )}
        </CCol>

        {/* ACTIONS */}
        <CCol md={12}>
          <CLoadingButton type="submit" color="success" loading={loader}>
            Submit
          </CLoadingButton>

          <CLink className="mx-2" onClick={() => props.toggleDetails?.(props.prizeId)}>
            Cancel
          </CLink>
        </CCol>
      </CForm>
    </>
  )
}

export default EditForm
